import styled from 'styled-components';
import React, { useEffect, useState, useMemo } from "react";
import { useSelector, shallowEqual } from 'react-redux';
import AnalyticsBreadcrumb from "ProtoType/NewAnalytics/AnalyticsBreadcrumb";
import Empty from "../components/EmptyFeedback";
import { calculate_outcome_summary_chart } from '../factor/calculations';
import { useCustomDataRedirect } from "../common/useCustomDataRedirect";
import { validateAndSanitizeOutcomes } from "../utils/outcomesDataIntegrity";

const CorrelationAnalytics = ({
  core_data,
  outcomeQ: outcomeQProp,
  data,
  lastData,
  outcomeStandards,
  navigation,
  rule,
}) => {
  // Redirect protection for custom data from NewAnalytics
  useCustomDataRedirect();

  // Sanitize outcomeQ data to fix duplicate IDs
  const outcomeQ = useMemo(() => {
    if (!outcomeQProp) return null;
    const { sanitized, integrity } = validateAndSanitizeOutcomes(outcomeQProp);
    if (integrity.hasDuplicates) {
      console.log('[Correlations] Outcome data sanitized:', { integrity });
    }
    return sanitized;
  }, [outcomeQProp]);

  const [isEmpty, setIsEmpty] = useState(false);
  const [correlationMatrix, setCorrelationMatrix] = useState(null);
  const [selectedOutcome, setSelectedOutcome] = useState(null);

  // Calculate Pearson correlation coefficient
  const calculateCorrelation = (x, y) => {
    const n = x.length;
    if (n === 0) return 0;
    
    const sumX = x.reduce((a, b) => a + b, 0);
    const sumY = y.reduce((a, b) => a + b, 0);
    const sumXY = x.reduce((total, xi, i) => total + xi * y[i], 0);
    const sumX2 = x.reduce((total, xi) => total + xi * xi, 0);
    const sumY2 = y.reduce((total, yi) => total + yi * yi, 0);
    
    const numerator = n * sumXY - sumX * sumY;
    const denominator = Math.sqrt((n * sumX2 - sumX * sumX) * (n * sumY2 - sumY * sumY));
    
    return denominator === 0 ? 0 : numerator / denominator;
  };

  // Text normalizer function from calculations.js
  function normalise(str) {
    if (typeof str !== 'string') return '';
    return str
      .toLowerCase()
      .replace(/\{.*?}/g, '')      // strip {entity} placeholder
      .replace(/\u2026/g, '...')   // … → ...
      .replace(/[.\s]+$/, '')      // trailing dots / spaces
      .replace(/\s+/g, ' ')        // collapse whitespace
      .trim();
  }

  // Check if a response is a valid numeric value (not N/A)
  const isValidResponse = (response) => {
    if (response === null || response === undefined) return false;
    if (typeof response === 'string') {
      const trimmed = response.trim().toLowerCase();
      // Filter out common N/A patterns
      if (trimmed === '' || trimmed === 'n/a' || trimmed === 'na' || 
          trimmed === 'not applicable' || trimmed === 'no response' ||
          trimmed === 'prefer not to answer' || trimmed === 'skip') {
        return false;
      }
      // Try to parse as number
      const parsed = parseFloat(response);
      return !isNaN(parsed) && isFinite(parsed);
    }
    if (typeof response === 'number') {
      // Filter out common N/A numeric codes
      if (response === -1 || response === 999 || response === -999 || 
          !isFinite(response) || isNaN(response)) {
        return false;
      }
      return true;
    }
    return false;
  };

  // Extract numeric values from survey responses
  const extractValues = (responses, questionId) => {
    if (!responses || !Array.isArray(responses)) return [];
    
    return responses
      .map(response => {
        if (response && response.responses && Array.isArray(response.responses)) {
          const questionResponse = response.responses.find(r => r.question_id === questionId);
          if (questionResponse && isValidResponse(questionResponse.response)) {
            return typeof questionResponse.response === 'string' 
              ? parseFloat(questionResponse.response) 
              : questionResponse.response;
          }
        }
        return null;
      })
      .filter(val => val !== null);
  };

  // Calculate correlation matrix
  // 
  // N/A HANDLING STRATEGY:
  // - Individual N/A responses are filtered out when calculating averages for each respondent
  // - A respondent is completely excluded from correlation if they don't meet minimum thresholds:
  //   * Factor: Must have valid responses to at least 50% of factor questions
  //   * Outcome: Must have valid responses to at least 50% of outcome questions
  // - Only respondents with BOTH valid factor AND outcome averages are included
  // - This ensures perfectly aligned factor and outcome score arrays for correlation
  //
  const calculateCorrelationMatrix = useMemo(() => {
    if (!data || !core_data || !outcomeQ || 
        !core_data.questions || !core_data.questions.dimensions || 
        !outcomeQ.questions) return null;

    const factors = [];
    const outcomes = [];
    
    // Collect all factors (following calculations.js pattern)
    core_data.questions.dimensions.forEach((dimension, dimIndex) => {
      if (dimension && dimension.factors && Array.isArray(dimension.factors)) {
        dimension.factors.forEach((factor, factorIndex) => {
          if (factor && factor.title && factor.questions && Array.isArray(factor.questions)) {
            factors.push({
              title: factor.title,
              dimension: dimIndex,
              factorIndex: factorIndex,
              questionIndices: factor.questions.map((q, qIndex) => qIndex), // question indices in factor
              questions: factor.questions // keep original questions for reverse scoring
            });
          }
        });
      }
    });

    // Collect all outcome sections (aggregates, not individual questions)
    if (outcomeQ && outcomeQ.questions) {
      outcomeQ.questions.forEach(section => {
        if (section && section.name && section.id) {
          outcomes.push({
            id: section.id,
            name: section.name,
            section: section
          });
        }
      });
    }

    const matrix = [];

    factors.forEach(factor => {
      const factorRow = {
        factor: factor.title,
        dimension: factor.dimension,
        correlations: []
      };

             outcomes.forEach(outcome => {
         // Arrays to store valid factor and outcome scores (perfectly aligned)
         const factorScores = [];
         const outcomeScores = [];
         
         data.forEach(response => {
           // STEP 1: Calculate factor score for this respondent
           const factorValues = [];
           
           // Process culture factor questions (from response.questions)
           if (response.questions && Array.isArray(response.questions)) {
             for (const question of response.questions) {
               // Check if question belongs to this factor
               if (
                 question.id == factor.dimension && // dimension ID
                 question.factor == factor.factorIndex && // factor index
                 factor.questionIndices && factor.questionIndices.includes(question.q) // question index
               ) {
                 let resp = question.response;
                 if (isValidResponse(resp)) {
                   // Convert string to number if needed
                   if (typeof resp === 'string') {
                     resp = parseFloat(resp);
                   }
                   
                   // Apply reverse scoring if needed
                   const qIndex = question.q;
                   const questionObj = factor.questions[qIndex];
                   if (questionObj && questionObj.reverse) {
                     resp = 10 - resp;
                   }
                   
                   factorValues.push(resp);
                 }
               }
             }
           }
           
           // STEP 2: Calculate outcome score for this respondent
           const outcomeValues = [];
           
           if (response.employee_outcomes && response.employee_outcomes.responses) {
             // Get all responses for this outcome section
             outcome.section.questions.forEach(q => {
               const outcomeResponse = response.employee_outcomes.responses.find(r => {
                 return normalise(r.question) === normalise(q.question);
               });
               
               if (outcomeResponse && isValidResponse(outcomeResponse.response)) {
                 const val = typeof outcomeResponse.response === 'string' 
                   ? parseFloat(outcomeResponse.response) 
                   : outcomeResponse.response;
                 outcomeValues.push(val);
               }
             });
           }
           
           // STEP 3: Only include this respondent if BOTH factor and outcome meet thresholds
           const minFactorQuestions = Math.ceil((factor.questionIndices?.length || 1) * 0.5);
           const minOutcomeQuestions = Math.ceil((outcome.section.questions?.length || 1) * 0.5);
           
           const hasValidFactor = factorValues.length >= minFactorQuestions;
           const hasValidOutcome = outcomeValues.length >= minOutcomeQuestions;
           
           if (hasValidFactor && hasValidOutcome) {
             // Calculate averages
             const avgFactorScore = factorValues.reduce((a, b) => a + b, 0) / factorValues.length;
             const avgOutcomeScore = outcomeValues.reduce((a, b) => a + b, 0) / outcomeValues.length;
             
             // Add to aligned arrays
             factorScores.push(avgFactorScore);
             outcomeScores.push(avgOutcomeScore);
           }
           // If either factor or outcome is invalid, exclude this respondent entirely
         });

         // Calculate correlation if we have enough data points
         // Arrays are guaranteed to be the same length and properly aligned
         const correlation = factorScores.length >= 3
           ? calculateCorrelation(factorScores, outcomeScores)
           : 0;

         console.log(`Correlation: ${factor.title} vs ${outcome.name}`, {
           factorScores: factorScores.slice(0, 5), // Show first 5 for debugging
           outcomeScores: outcomeScores.slice(0, 5),
           totalDataPoints: factorScores.length,
           correlation: correlation
         });

         factorRow.correlations.push({
           outcome: outcome.name,
           value: correlation,
           dataPoints: factorScores.length
         });
       });

      matrix.push(factorRow);
    });

    console.log('Correlations - collected factors:', factors);
    console.log('Correlations - collected outcomes:', outcomes);
    console.log('Correlations - correlation matrix:', matrix);

    // Return null if no factors or outcomes found
    if (factors.length === 0 || outcomes.length === 0) {
      console.log('Correlations - no factors or outcomes found');
      return null;
    }

    // Calculate average correlations for sorting
    const factorAverages = matrix.map(row => {
      const validCorrelations = row.correlations
        .map(c => c.value)
        .filter(val => !isNaN(val) && isFinite(val));
      const average = validCorrelations.length > 0 
        ? validCorrelations.reduce((a, b) => a + b, 0) / validCorrelations.length 
        : 0;
      return {
        ...row,
        averageCorrelation: Math.abs(average) // Use absolute value for sorting
      };
    });

    const outcomeAverages = outcomes.map((outcome, outcomeIndex) => {
      const outcomeCorrelations = matrix
        .map(row => row.correlations[outcomeIndex]?.value || 0)
        .filter(val => !isNaN(val) && isFinite(val));
      const average = outcomeCorrelations.length > 0
        ? outcomeCorrelations.reduce((a, b) => a + b, 0) / outcomeCorrelations.length
        : 0;
      return {
        ...outcome,
        averageCorrelation: Math.abs(average) // Use absolute value for sorting
      };
    });

    // Sort by highest absolute average correlation
    const sortedFactors = factorAverages.sort((a, b) => b.averageCorrelation - a.averageCorrelation);
    const sortedOutcomes = outcomeAverages.sort((a, b) => b.averageCorrelation - a.averageCorrelation);

    // Rebuild matrix in sorted order with proper correlation mapping
    const sortedMatrix = sortedFactors.map(sortedFactor => {
      const originalRow = matrix.find(row => row.factor === sortedFactor.factor);
      const sortedCorrelations = sortedOutcomes.map(sortedOutcome => {
        return originalRow.correlations.find(corr => corr.outcome === sortedOutcome.name);
      });

      return {
        ...originalRow,
        correlations: sortedCorrelations,
        averageCorrelation: sortedFactor.averageCorrelation
      };
    });

    // Calculate final averages for display (using actual correlation values, not absolute)
    const finalFactorAverages = sortedMatrix.map(row => {
      const validCorrelations = row.correlations
        .map(c => c.value)
        .filter(val => !isNaN(val) && isFinite(val));
      return validCorrelations.length > 0 
        ? validCorrelations.reduce((a, b) => a + b, 0) / validCorrelations.length 
        : 0;
    });

    const finalOutcomeAverages = sortedOutcomes.map((outcome, outcomeIndex) => {
      const outcomeCorrelations = sortedMatrix
        .map(row => row.correlations[outcomeIndex]?.value || 0)
        .filter(val => !isNaN(val) && isFinite(val));
      return outcomeCorrelations.length > 0
        ? outcomeCorrelations.reduce((a, b) => a + b, 0) / outcomeCorrelations.length
        : 0;
    });

    return {
      factors: sortedFactors.map(f => ({ name: f.factor, factor: f.factor })), // Row labels (factors)
      outcomes: sortedOutcomes, // Column headers (outcomes)
      matrix: sortedMatrix,
      factorAverages: finalFactorAverages,
      outcomeAverages: finalOutcomeAverages
    };
  }, [data, core_data, outcomeQ]);

  useEffect(() => {
    console.log('Correlations - received data:', data);
    console.log('Correlations - core_data:', core_data);
    console.log('Correlations - outcomeQ:', outcomeQ);

    if (!data || data.length === 0) {
      console.log('Correlations - no data available');
      setIsEmpty(true);
      return;
    }

    setIsEmpty(false);
  }, [data]);

  useEffect(() => {
    setCorrelationMatrix(calculateCorrelationMatrix);
    // Set first outcome as selected by default
    if (calculateCorrelationMatrix && calculateCorrelationMatrix.outcomes &&
      calculateCorrelationMatrix.outcomes.length > 0 && !selectedOutcome) {
      setSelectedOutcome(calculateCorrelationMatrix.outcomes[0].name);
    }
  }, [calculateCorrelationMatrix]);

  // Get relationship strength label
  const getRelationshipStrength = (correlation) => {
    const abs = Math.abs(correlation);
    if (abs > 0.70) return 'Strong';
    if (abs > 0.30) return 'Moderate';
    return 'Weak';
  };

  // Get relationship strength color
  const getStrengthColor = (correlation) => {
    const abs = Math.abs(correlation);
    if (abs > 0.70) return '#327396';
    if (abs > 0.30) return '#66BFD3';
    return '#C9EFF5';
  };

  // Get color for correlation value
  const getCorrelationColor = (correlation) => {
    const abs = Math.abs(correlation);
    if (abs >= 0.7) return correlation > 0 ? '#1a5c1a' : '#8b0000';
    if (abs >= 0.5) return correlation > 0 ? '#2d8f2d' : '#cd5c5c';
    if (abs >= 0.3) return correlation > 0 ? '#4caf50' : '#f08080';
    if (abs >= 0.1) return correlation > 0 ? '#81c784' : '#ffa07a';
    return '#f5f5f5';
  };

  const getTextColor = (correlation) => {
    const abs = Math.abs(correlation);
    return abs >= 0.3 ? '#ffffff' : '#2A3039';
  };

  if (!core_data) {
    return <div>Loading...</div>;
  }

  // Get sorted drivers for selected outcome
  const getSortedDriversForOutcome = () => {
    if (!correlationMatrix || !selectedOutcome) return [];

    const outcomeIndex = correlationMatrix.outcomes.findIndex(o => o.name === selectedOutcome);
    if (outcomeIndex === -1) return [];

    // Get all drivers with their correlations for this outcome
    const driversWithCorr = correlationMatrix.matrix.map(row => ({
      driver: row.factor,
      correlation: row.correlations[outcomeIndex]
    }));

    // Sort by absolute correlation value (highest first)
    return driversWithCorr.sort((a, b) => Math.abs(b.correlation.value) - Math.abs(a.correlation.value));
  };

  const sortedDrivers = getSortedDriversForOutcome();

  return (
    <Container>
      <AnalyticsBreadcrumb currentComponent="correlations" />
      <Title>
        <TitleIconWrapper aria-hidden>
          <CorrelationsIcon />
        </TitleIconWrapper>
        Category Correlations
      </Title>
      {/* <Description>
        This heatmap shows the correlation between drivers and overall business outcome scores.
        Each outcome represents the aggregate score across all questions in that outcome category.
        Factors and outcomes are sorted by their highest average absolute correlations.
        Average correlations are displayed on the right (factor averages) and bottom (outcome averages).
        Darker colors indicate stronger correlations (positive in green, negative in red).
        N/A responses are excluded, and correlations require at least 3 matched data points.
        Cells marked with * have low sample sizes ({'<'}10) and should be interpreted with caution.
      </Description> */}

      {(!isEmpty && correlationMatrix) ? (
        <Content>
          {/* Outcome Toggle Buttons */}
          <OutcomeButtonsContainer>
            {correlationMatrix.outcomes.map(outcome => (
              <OutcomeButton
                key={outcome.name}
                isActive={selectedOutcome === outcome.name}
                onClick={() => setSelectedOutcome(outcome.name)}
              >
                {outcome.name}
              </OutcomeButton>
            ))}
          </OutcomeButtonsContainer>

          {/* Driver List View */}
          <DriverListContainer>
            <ListHeader>
              <HeaderColumn width="25%">CATEGORY</HeaderColumn>
              <HeaderColumn width="47%"></HeaderColumn>
              <HeaderColumn width="12%">CORRELATION (R)</HeaderColumn>
              <HeaderColumn width="18%">RELATIONSHIP STRENGTH</HeaderColumn>
            </ListHeader>

            {sortedDrivers.map((item, index) => {
              const corr = item.correlation;
              const strength = getRelationshipStrength(corr.value);
              const barWidth = Math.abs(corr.value) * 100;
              const isNegative = corr.value < 0;

              return (
                <DriverRow key={index}>
                  <DriverName>{item.driver}</DriverName>
                  <BarContainer>
                    <CorrelationBar
                      width={barWidth}
                      color={getStrengthColor(corr.value)}
                      isNegative={isNegative}
                    />
                  </BarContainer>
                  <CorrelationValue isNegative={isNegative}>
                    {corr.dataPoints >= 3 ? corr.value.toFixed(2) : 'N/A'}
                    {corr.dataPoints < 10 && corr.dataPoints >= 3 && (
                      <LowConfidenceIndicator title="Low sample size - interpret with caution">*</LowConfidenceIndicator>
                    )}
                  </CorrelationValue>
                  <StrengthBadgeContainer>
                    <StrengthBadge strength={strength}>
                      {strength}
                    </StrengthBadge>
                  </StrengthBadgeContainer>
                </DriverRow>
              );
            })}
          </DriverListContainer>

          {/* <InfoNote>
            <strong>Understanding Correlations:</strong> Correlation coefficients (R) measure the strength of relationships between drivers and outcomes.
            Values range from -1 to +1. In practice, culture drivers typically show positive correlations (0 to +1), meaning improvement in the driver
            associates with improvement in the outcome. Negative correlations (-1 to 0) are rare but would indicate an inverse relationship.
            The legend below shows all possible ranges, though your data may only contain positive correlations.
          </InfoNote> */}
          {/*
          <Legend>
            <LegendTitle>Correlation Strength Reference</LegendTitle>
            <LegendRow>
              <LegendItem>
                <LegendColor bgColor="#1a5c1a" />
                <span>Strong Positive (≥0.7)</span>
              </LegendItem>
              <LegendItem>
                <LegendColor bgColor="#2d8f2d" />
                <span>Moderate Positive (0.5-0.7)</span>
              </LegendItem>
              <LegendItem>
                <LegendColor bgColor="#4caf50" />
                <span>Weak Positive (0.3-0.5)</span>
              </LegendItem>
              <LegendItem>
                <LegendColor bgColor="#f5f5f5" />
                <span>Negligible (0-0.3)</span>
              </LegendItem>
              <LegendItem>
                <LegendColor bgColor="#ffa07a" />
                <span>Weak Negative (-0.3-0)</span>
              </LegendItem>
              <LegendItem>
                <LegendColor bgColor="#f08080" />
                <span>Moderate Negative (-0.5--0.3)</span>
              </LegendItem>
              <LegendItem>
                <LegendColor bgColor="#8b0000" />
                <span>Strong Negative (≤-0.5)</span>
              </LegendItem>
            </LegendRow>
          </Legend> */}
        </Content>
      ) : (
        <Empty addMargin={false} />
      )}
    </Container>
  );
};

export default CorrelationAnalytics;

const CorrelationsIcon = () => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M20 12C20 16.4183 16.4183 20 12 20M20 12C20 7.58172 16.4183 4 12 4M20 12H22M12 20C7.58172 20 4 16.4183 4 12M12 20V22M4 12C4 7.58172 7.58172 4 12 4M4 12H2M12 4V2M15 12C15 13.6569 13.6569 15 12 15C10.3431 15 9 13.6569 9 12C9 10.3431 10.3431 9 12 9C13.6569 9 15 10.3431 15 12Z"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const Container = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  font-family: 'Raleway';
`;

const BlueTitle = styled.div`
  color: #2D70E2;
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 15px;
  margin-top: -20px;
`;

const Title = styled.div`
  font-size: 28px;
  font-weight: bold;
  margin-bottom: 30px;
  color: #2A3039;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const TitleIconWrapper = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  background: #F8F9FA;
  border-radius: 7px;
  color: #1F2937;
`;

const Description = styled.div`
  font-size: 14px;
  font-weight: 400;
  margin-bottom: 30px;
  line-height: 1.5;
  color: #2A3039;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 100%;
  height: 100%;
  margin-top: 20px;
`;

const OutcomeButtonsContainer = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 30px;
  flex-wrap: wrap;
`;

const OutcomeButton = styled.button`
  padding: 10px 20px;
  font-size: 14px;
  font-weight: 600;
  border-radius: 8px;
  border: 2px solid ${props => props.isActive ? '#327396' : '#E5E7EB'};
  background: ${props => props.isActive ? '#327396' : '#ffffff'};
  color: ${props => props.isActive ? '#ffffff' : '#6B7280'};
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: 'Raleway', sans-serif;

  &:hover {
    border-color: #327396;
    background: ${props => props.isActive ? '#285E78' : '#F9FAFB'};
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(50, 115, 150, 0.1);
  }
`;

const DriverListContainer = styled.div`
  background: white;
  border-radius: 8px;
  border: 1px solid #E5E7EB;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
`;

const ListHeader = styled.div`
  display: flex;
  background: #F9FAFB;
  padding: 12px 20px;
  border-bottom: 2px solid #E5E7EB;
`;

const HeaderColumn = styled.div`
  flex: ${props => props.width ? `0 0 ${props.width}` : '1'};
  font-size: 11px;
  font-weight: 700;
  color: #6B7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  text-align: center;
  
  &:first-child {
    text-align: left;
  }
`;

const DriverRow = styled.div`
  display: flex;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #F3F4F6;
  transition: background 0.15s ease;

  &:hover {
    background: #F9FAFB;
  }

  &:last-child {
    border-bottom: none;
  }
`;

const DriverName = styled.div`
  flex: 0 0 25%;
  font-size: 14px;
  font-weight: 600;
  color: #1F2937;
  padding-right: 16px;
`;

const BarContainer = styled.div`
  flex: 0 0 45%;
  height: 24px;
  background: #F3F4F6;
  border-radius: 4px;
  overflow: hidden;
  margin-right: 16px;
`;

const CorrelationBar = styled.div`
  height: 100%;
  width: ${props => props.width}%;
  background: ${props => props.color};
  border-radius: 4px;
  transition: width 0.3s ease;
  ${props => props.isNegative && `
    background: linear-gradient(to left, ${props.color}, #f08080);
  `}
`;

const CorrelationValue = styled.div`
  flex: 0 0 12%;
  font-size: 14px;
  font-weight: 700;
  color: ${props => props.isNegative ? '#DC2626' : '#1F2937'};
  text-align: center;
`;

const StrengthBadgeContainer = styled.div`
  flex: 0 0 18%;
  text-align: center;
`;

const StrengthBadge = styled.span`
  font-size: 12px;
  font-weight: 600;
  padding: 4px 12px;
  border-radius: 12px;
  background: ${props => {
    if (props.strength === 'Strong') return '#327396';
    if (props.strength === 'Moderate') return '#66BFD3';
    if (props.strength === 'Weak') return '#C9EFF5';
    return '#E5E7EB';
  }};
  color: ${props => props.strength === 'Weak' ? '#1F2937' : '#ffffff'};
  display: inline-block;
  width: 100%;
`;

const InfoNote = styled.div`
  margin-top: 30px;
  padding: 16px;
  background: #EFF6FF;
  border-left: 4px solid #4A90E2;
  border-radius: 4px;
  font-size: 13px;
  line-height: 1.6;
  color: #1F2937;

  strong {
    color: #1F2937;
  }
`;

const LowConfidenceIndicator = styled.span`
  color: #ff9800;
  font-weight: bold;
  margin-left: 2px;
  font-size: 14px;
`;

const Legend = styled.div`
  margin-top: 30px;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 8px;
`;

const LegendTitle = styled.div`
  font-weight: 600;
  color: #2A3039;
  margin-bottom: 15px;
  font-size: 16px;
`;

const LegendRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #2A3039;
`;

const LegendColor = styled.div`
  width: 20px;
  height: 20px;
  background-color: ${props => props.bgColor};
  border-radius: 3px;
  border: 1px solid #ddd;
`;